import { useNavigate } from 'react-router';
import FileSaver from 'file-saver';
import {
    Box,
    Flex,
    Heading,
    Text,
    Button,
    Icon,
    useToast,
    useDisclosure,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
} from '@chakra-ui/react';
import PageHeader from 'layout/pageHeader';
import MDropZone from 'components/molecules/MDropZone';
import { ChangeEvent, useState } from 'react';
import { EXCEL_FILE_EXTENSION, EXCEL_FILE_TYPE } from 'shared/const';
import icons from 'shared/icons';
import { useLazyExportUsersQuery } from 'core/API/usersApi';
import { intentionalyFloatingPromiseReturn } from 'utils/wrappers';
import { IExcelUser } from 'pages/UserManagement/types';
import { useTranslation } from 'helpers/languages/translations';
import { useExportSectoExcelMutation, useUploadSectoExcelMutation } from 'core/API/sectoApi';
import DescriptionHtml from 'components/molecules/DescriptionHtml';
import { useGetRetailerByClientQuery } from 'core/API/authApi';
import RSelect from 'components/atoms/formInputs/RSelect';
import { orderBy } from 'lodash';

const SectoUpload = () => {
    const translate = useTranslation();
    const navigate = useNavigate();
    const toast = useToast();
    const [uploadSecto, { isLoading: isUploadLoading }] = useUploadSectoExcelMutation();
    const [exportTrigger, { isLoading: isExportLoading }] = useLazyExportUsersQuery();
    const { isOpen: isModalErrorOpen, onOpen: onOpenModalErrorUpload, onClose: onModalErrorClose } = useDisclosure();
    const [exportSectoMutation, { isLoading: isExportTemplateLoading }] = useExportSectoExcelMutation();

    const [usersErrors, setUsersErrors] = useState<IExcelUser[]>([]);
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [selectedTool, setSelectedTool] = useState<number>(2);
    const [retailerIds, setRetailerIds] = useState<(string | number)[]>([]);
    const goBack = () => navigate(-1);

    const { data: retailersData } = useGetRetailerByClientQuery();

    const handleChangeFile = (file: File | null) => {
        if (file?.name.endsWith(EXCEL_FILE_EXTENSION)) {
            setUploadedFile(file);
        } else if (file === null) {
            setUploadedFile(null);
        }
    };

    const handleDownloadError = async () => {};

    const handleUpload = async () => {
        try {
            const bodyFormData = new FormData();
            bodyFormData.append('excelFile', uploadedFile as File);
            bodyFormData.append('toolId', `${selectedTool}`);

            const secto = await uploadSecto(bodyFormData);

            if ('error' in secto) {
                try {
                    toast({
                        title: translate('secto.upload.error'),
                        status: 'error',
                        isClosable: true,
                    });

                    const blob = new Blob([(secto as any).error.data], {
                        type: EXCEL_FILE_TYPE,
                    });

                    const timeElapsed = Date.now();
                    const today = new Date(timeElapsed);
                    FileSaver.saveAs(blob, `secto_error_${today.toISOString()}.xlsx`);
                } catch (error) {
                    console.error(error);
                }
            } else {
                goBack();

                toast({
                    title: `succesfully uploaded users`,
                    status: 'success',
                    isClosable: true,
                });
            }
        } catch {
            toast({
                title: 'error uploading file',
                status: 'error',
                isClosable: true,
            });
        }
    };

    const renderModal = () => {
        return (
            <Modal isOpen={isModalErrorOpen} onClose={onModalErrorClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{translate('users.upload.modal.title')}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {translate('users.upload.modal.body_1')} {usersErrors.length}{' '}
                        {translate('users.upload.modal.body_2')}
                    </ModalBody>

                    <ModalFooter>
                        <Button mr={3} variant="ghost" onClick={onModalErrorClose}>
                            {translate('users.upload.modal.close')}
                        </Button>
                        <Button colorScheme="blue" onClick={intentionalyFloatingPromiseReturn(handleDownloadError)}>
                            {translate('users.upload.modal.validate.error')}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        );
    };

    const renderDescriptionSection = () => {
        const DownloadIcon = icons.download;
        const UploadIcon = icons.upload;

        const handleExportTemplate = async () => {
            try {
                const { data: res, isError } = await exportTrigger({ isTemplate: true });
                if (isError || !res) throw new Error('an Error occured when downloading file');

                const blob = new Blob([res], {
                    type: EXCEL_FILE_TYPE,
                });

                // todo: get the name of the user here or company info
                FileSaver.saveAs(blob, 'user_template.xlsx');
            } catch (error) {
                toast({
                    title: (error as Error).message,
                    status: 'error',
                    isClosable: true,
                });
            }
        };

        const onChangeSelectTool = (e: ChangeEvent<HTMLSelectElement>) => {
            setSelectedTool(Number(e.target.value));
        };

        const handleDownloadTemplate = async () => {
            try {
                const res = await exportSectoMutation({ toolId: selectedTool, retailerIds });
                if ('error' in res) throw new Error('something wrong occured when exporting users excel');

                const blob = new Blob([res.data], {
                    type: EXCEL_FILE_TYPE,
                });

                const timeElapsed = Date.now();
                const today = new Date(timeElapsed);
                FileSaver.saveAs(blob, `secto_${today.toISOString()}.xlsx`);
                toast({
                    title: 'succesfully exported secto excel',
                    status: 'success',
                    isClosable: true,
                });
            } catch (error) {
                toast({
                    title: (error as Error).message,
                    status: 'error',
                    isClosable: true,
                });
            }
        };

        const retailersOptions =
            orderBy(
                retailersData?.data.map((ret) => ({ label: ret.name, value: ret.id })),
                'label',
            ) || [];

        return (
            <Flex w="45%" justifyContent="flex-start" direction="column" textAlign="start">
                <Text>{translate('secto.upload.title')}</Text>
                <Text>{translate('users.upload.body')}</Text>

                <Flex mt="24px" justifyContent="space-between" gap={2} alignItems="flex-start">
                    <RSelect options={retailersOptions} onChange={setRetailerIds} selectedOptions={retailerIds} />
                    <Button w="40%" onClick={handleDownloadTemplate} my={2} isDisabled={!retailerIds.length}>
                        {translate('secto.upload.templateButton')}
                    </Button>
                </Flex>

                <Flex mt="24px" justifyContent="space-between">
                    <Select value={selectedTool} onChange={onChangeSelectTool} placeholder="Select option">
                        <option value={2}>Datafield</option>
                        <option value={1}>Dashboard</option>
                    </Select>
                </Flex>

                {renderModal()}
                <Box py={4}>
                    <MDropZone chosenFile={uploadedFile} handleChangeFile={handleChangeFile} />
                </Box>
                <Flex mt="24px" justifyContent="space-between">
                    <Button
                        onClick={intentionalyFloatingPromiseReturn(handleUpload)}
                        isLoading={isUploadLoading}
                        isDisabled={!uploadedFile}
                        rightIcon={<Icon as={UploadIcon} />}
                        colorScheme="blue"
                        bg="blueMain"
                    >
                        {translate('users.upload.button')}
                    </Button>
                </Flex>
            </Flex>
        );
    };

    return (
        <Box>
            <PageHeader pageTitle={`Secto / Upload`} icon={{ name: 'arrowBack', props: { onClick: goBack } }} />
            <Box px="32px">
                <Flex justifyContent="start">
                    <Heading as="h2" fontWeight="semibold" fontSize="18px" mb="18px">
                        {translate('product.upload.uploadFile')}
                    </Heading>
                </Flex>
                <Flex justifyContent="flex-start" direction="row" textAlign="start" gap={5}>
                    {renderDescriptionSection()}
                    <DescriptionHtml h="fit-content" tradKey="secto.upload.description" />
                </Flex>
            </Box>
        </Box>
    );
};

export default SectoUpload;
